import { findLotModelByNumber } from 'utils/stationLotModel'

import {
  ActionType,
  allowsEmptyScenarioActionSelection,
  ILotModelConfig,
  IServicePoint,
  LotType,
  SERVICE_POINT_KINDS,
} from 'utils/servicePointsCatalog'

type LegacyMaintenance = {
  parking?: boolean
  charging?: boolean
  washing?: boolean
  waiting_points?: { position: number; time: number }[]
  service_points?: unknown
}

export const CHARGING_MUST_BE_LAST_MSG = 'A charging station must be placed at the end of the scenario'

export const SERVICE_POINT_ACTION_REQUIRED_MSG = 'Select an action for the service point'

type MaintenancePointRef = { facility_index?: number | null; type?: string }

/**
 * Empty actions on a known charging lot that is not last use the placement message.
 * Any other empty-action failure (unknown lot model, point already last) does not.
 */
export function messageForEmptyScenarioActions(
  points: IServicePoint[],
  lotModels: ILotModelConfig[],
  maintenancePoints: MaintenancePointRef[],
  options: { missionCount: number; hasChargingLotInScenario: boolean; robotType?: string | null }
): string {
  let chargingNotLast = false
  let otherEmpty = false
  points.forEach((sp, index) => {
    if (sp.action_type.length > 0) {
      return
    }
    const isLast = index === points.length - 1 && sp.position >= options.missionCount
    if (
      allowsEmptyScenarioActionSelection(sp, lotModels, maintenancePoints, {
        isLast,
        hasChargingLotInScenario: options.hasChargingLotInScenario,
        robotType: options.robotType,
      })
    ) {
      return
    }
    const modelConfig =
      sp.kind === SERVICE_POINT_KINDS.STATION_LOT && sp.lot_model
        ? findLotModelByNumber(lotModels, sp.lot_model)
        : null
    if (modelConfig?.type === LotType.CHARGING && !isLast) {
      chargingNotLast = true
    } else {
      otherEmpty = true
    }
  })
  if (chargingNotLast && !otherEmpty) {
    return CHARGING_MUST_BE_LAST_MSG
  }
  return SERVICE_POINT_ACTION_REQUIRED_MSG
}

/** True while the CHARGING service action is selected on this SP. */
export function isChargingAnchor(sp: IServicePoint, _lotModels: ILotModelConfig[] = []): boolean {
  return sp.action_type.includes(ActionType.CHARGING)
}

/** True when a CHARGING action exists but that SP is not the last row in display order. */
export function hasChargingNotAtEnd(
  displayOrder: Array<{ type: 'mission' } | { type: 'sp'; sp: IServicePoint }>,
  lotModels: ILotModelConfig[] = []
): boolean {
  const chargingIndexes: number[] = []
  displayOrder.forEach((item, index) => {
    if (item.type === 'sp' && isChargingAnchor(item.sp, lotModels)) {
      chargingIndexes.push(index)
    }
  })
  if (chargingIndexes.length === 0) return false
  return Math.max(...chargingIndexes) !== displayOrder.length - 1
}

/** Backend rule: at most one CHARGING, and only in the post-mission block. */
export function chargingPlacementError(points: IServicePoint[], missionCount: number): string | null {
  const charging = points.filter(sp => sp.action_type.includes(ActionType.CHARGING))
  if (charging.length > 1) {
    return 'At most one service point with CHARGING is allowed.'
  }
  if (charging.length === 1 && charging[0].position < missionCount) {
    return `CHARGING is only allowed at scenario end (position >= ${missionCount}).`
  }
  return null
}

/** When appending a mission, shift the whole end block (waits, docks, charging). */
export function shiftEndBlockServicePoints(
  points: IServicePoint[],
  oldMissionCount: number
): IServicePoint[] {
  return points.map(sp => {
    if (oldMissionCount === 0 || sp.position >= oldMissionCount) {
      return { ...sp, position: sp.position + 1 }
    }
    return sp
  })
}

export function servicePointsToLegacy(
  points: IServicePoint[],
  missionCount: number
): Required<Pick<LegacyMaintenance, 'parking' | 'charging' | 'washing' | 'waiting_points'>> {
  const postPosition = Math.max(missionCount, 0)
  let parking = false
  let charging = false
  let washing = false
  const waiting_points: { position: number; time: number }[] = []

  const waits = points
    .filter(sp => sp.kind === 'wait')
    .slice()
    .sort((a, b) => a.position - b.position || a.order - b.order)

  waits.forEach(sp => {
    waiting_points.push({ position: sp.position, time: sp.time_sec || 0 })
  })

  points.forEach(sp => {
    if (sp.kind !== 'station_lot' || sp.position < postPosition) return
    if (sp.action_type.includes(ActionType.PARKING)) parking = true
    if (sp.action_type.includes(ActionType.CHARGING)) charging = true
    if (sp.action_type.includes(ActionType.WASHING)) washing = true
  })

  return { parking, charging, washing, waiting_points }
}

export function servicePointsForBackend(points: IServicePoint[]) {
  return points.map(sp => ({
    ...sp,
    action_type: sp.action_type.map(a => ActionType[a]),
  }))
}

export function buildScenarioMaintenance(
  existing: LegacyMaintenance | undefined,
  points: IServicePoint[],
  missionCount: number
) {
  const legacy = servicePointsToLegacy(points, missionCount)
  return {
    ...(existing && typeof existing === 'object' ? existing : {}),
    ...legacy,
    service_points: servicePointsForBackend(points),
  }
}

export function formatApiErrorDetails(error: unknown): string {
  const body =
    error && typeof error === 'object' && 'body' in error
      ? (error as { body?: { details?: unknown } }).body
      : undefined
  const details = body?.details
  if (typeof details === 'string' && details.trim()) return details
  if (details && typeof details === 'object') {
    const record = details as Record<string, unknown>
    const maintenance = record.maintenance
    if (typeof maintenance === 'string') return maintenance
    if (Array.isArray(maintenance)) return maintenance.map(String).join(' ')
    const first = Object.values(record).find(value => typeof value === 'string' || Array.isArray(value))
    if (typeof first === 'string') return first
    if (Array.isArray(first)) return first.map(String).join(' ')
  }
  if (error instanceof Error && error.message) return error.message
  return 'Request failed'
}
